import React, {useEffect} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {DateInput} from '../../../common/component/DateInput'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {SelectInput} from '../../../common/component/SelectInput'
import {WeeklyRentBudgetInput} from '../../../common/component/WeeklyRentBudgetInput'
import WizardSectionCard from './sections/WizardSectionCard'
import {toOptionalBoolean} from '../wizard/wizardAccess'

interface PreferencesStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const leaseTermOptions = [
  {label: '6 months', value: '6-months'},
  {label: '12 months', value: '12-months'},
  {label: '24 months', value: '24-months'},
]

const moveInAsapOptions = [
  {label: 'As soon as possible', value: true},
  {label: 'Pick a date', value: false},
]

const PreferencesStep: React.FC<PreferencesStepProps> = ({
  register,
  control,
  errors,
  setValue,
  watch,
  triggerValidation,
}) => {
  const moveInAsap = toOptionalBoolean(watch('moveInAsap'))

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  const handleMoveInChoice = (value: string | number | boolean) => {
    const isAsap = toOptionalBoolean(value)
    if (isAsap === undefined) {
      return
    }

    setValue('moveInAsap', isAsap, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    if (isAsap) {
      setValue('moveInPreferredDate', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      })
    }
  }

  return (
    <div>
      <div className='wizard-hint-card mb-4 d-flex ' style={{padding: '20px'}}>
        <i className='bi bi-info-circle me-5 gradient-icon'></i>
        <span>This helps agents understand your timeline and what works for you.</span>
      </div>

      <div className='d-grid gap-4'>
        <WizardSectionCard title='' className='px-8 py-2'>
          <RadioButtonInput<IApplicationFormData>
            name='moveInAsap'
            label='When do you want to move in?'
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={moveInAsapOptions}
            value={moveInAsap}
            defaultValue={moveInAsap}
            onChange={handleMoveInChoice}
          />

          {moveInAsap === false && (
            <DateInput
              name='moveInPreferredDate'
              label=''
              control={control}
              errors={errors}
              required={true}
              className='mt-0'
            />
          )}

          <SelectInput
            className='mt-10'
            name='preferredLeaseTerm'
            label='Preferred lease length'
            register={register}
            errors={errors}
            required={true}
            optionsList={leaseTermOptions}
          />

          <WeeklyRentBudgetInput
            minName='rentWeeklyBudgetMin'
            maxName='rentWeeklyBudgetMax'
            control={control}
            errors={errors}
            className='mt-10'
          />
        </WizardSectionCard>
      </div>
    </div>
  )
}

export default PreferencesStep
